import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History, ChevronDown, ChevronUp, Play, ExternalLink, Film, Radio } from "lucide-react";
import type { HistoryEntry } from "@/lib/room-state";

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function HistoryPanel({ history, onLoadVideo }: HistoryPanelProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] to-transparent pointer-events-none" />
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors relative"
      >
 <span className="w-7 h-7 flex items-center justify-center">
          <History className="w-3.5 h-3.5" />
        </span>
        <span className="font-semibold tracking-tight">Playback history</span>
        <span className="text-[10px] font-mono glass no-border rounded-full px-2 py-1">{history.length}</span>
 <span className="ml-auto w-7 h-7 flex items-center justify-center">
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden relative"
          >
            <div className="px-4 pb-4 space-y-2 max-h-64 overflow-y-auto">
              {history.length === 0 ? (
                <p className="text-[11px] text-muted-foreground text-center py-4 glass rounded-xl">No videos played yet.</p>
              ) : (
                history.map((entry) => {
                  const Icon = entry.type === "hls" ? Radio : Film;
                  return (
                    <div key={entry.id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-secondary hover:bg-card border border-white/[0.04] text-xs transition-all group">
 <span className="w-8 h-8 flex items-center justify-center shrink-0">
                        <Icon className="w-3.5 h-3.5 text-primary" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{entry.title}</div>
                        <div className="text-[10px] text-muted-foreground truncate">by {entry.playedByName} · {relativeTime(entry.playedAt)}</div>
                      </div>
                      <div className="shrink-0 flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        {onLoadVideo && (
 <button onClick={() => onLoadVideo(entry.url, entry.type, entry.title)} className="w-7 h-7 flex items-center justify-center" title="Play again">
                            <Play className="w-3 h-3 fill-current" />
                          </button>
                        )}
 <a href={entry.url} target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center" title="Open original">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface HistoryPanelProps {
  history: HistoryEntry[];
  onLoadVideo?: (url: string, type: HistoryEntry["type"], title: string) => void;
}
